/**
 * Recuperar contraseña (Alta.dc.html, pantallas 4 y 4-ok).
 *
 * La confirmación es SIEMPRE la misma, exista o no la cuenta — y la propia
 * pantalla lo dice, que es más honesto que fingir.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword, apiMessage } from '../../services/apiClient';
import { Icon } from '../ui/Icon';
import { AuthLayout, CARD_SHADOW, MessageCard } from './AuthLayout';

export function RecuperarPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email);
      // Sólo si la petición LLEGÓ al servidor. El `finally` sin `catch`
      // pintaba la tarjeta de "acabamos de enviar un enlace" también cuando
      // la petición había muerto sin red: el anti-enumeración (misma
      // respuesta exista o no la cuenta) justifica el mensaje ante el
      // BACKEND, no ante la ausencia de red (ronda 3, r2b19).
      setSent(true);
    } catch (err: unknown) {
      setError(apiMessage(err, 'No se pudo enviar el enlace. Comprueba tu conexión e inténtalo de nuevo.'));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout>
        <MessageCard
          icon="mail"
          title="Si existe la cuenta, revísala"
          body="Si hay una cuenta con ese email, acabamos de enviar un enlace para crear una contraseña nueva."
        >
          <div className="bg-surface border border-line rounded-2xl px-4.5 py-4 text-left flex gap-3 items-start">
            <Icon name="shield" size={19} strokeWidth={1.8} className="flex-none mt-px text-ink-2" />
            <span className="text-[13.5px] text-ink-2">
              Por seguridad, este mensaje es el mismo exista o no una cuenta con ese email.
            </span>
          </div>
        </MessageCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div
        className="w-full max-w-[520px] self-start bg-canvas border border-line-strong rounded-3xl p-7 sm:p-11 animate-rise"
        style={CARD_SHADOW}
      >
        <Link to="/login" className="inline-flex items-center gap-1.5 text-ink-2 no-underline text-[14px] font-semibold mb-5">
          <Icon name="chevron-left" size={17} strokeWidth={2} />
          Volver
        </Link>
        <h1 className="font-display font-extrabold text-[26px] sm:text-[32px] tracking-tight mb-2.5">
          Recuperar contraseña
        </h1>
        <p className="text-ink-2 text-[15.5px] leading-relaxed mb-6">
          Escribe tu email y te enviamos un enlace para crear una nueva. El enlace caduca en 1 hora.
        </p>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="label !mb-0">Email</span>
            <input
              className="field bg-surface"
              type="email"
              placeholder="marta@barlaplaza.es"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {error && (
            <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14.5px]">{error}</p>
          )}
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? 'Enviando…' : 'Enviar enlace'}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}
